import React, { Fragment, useState } from "react";
import { Row, Col, Table, Button } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnFiltersState,
  FilterFn,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";

// Define Props
interface TableContainerProps {
  columns: any[];
  data: any[];
  divClassName?: string;
  tableClass?: string;
  theadClass?: string;
  isBordered?: boolean;
  isGlobalFilter?: boolean;
  isPagination?: boolean;
  paginationWrapper?: string;
  SearchPlaceholder?: string;
  pagination?: string;
  paginationClass?: string;
  handleUserClick?: () => void;
  buttonClass?: string;
  buttonName?: string;
  isAddButton?: boolean;
  isJobListGlobalFilter?: boolean;
  customPageSize?: number;
}

const TableContainer = ({
  columns,
  data,
  divClassName,
  tableClass,
  theadClass,
  isBordered,
  isGlobalFilter,
  isPagination,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  paginationClass,
  handleUserClick,
  buttonClass,
  buttonName,
  isAddButton,
  isJobListGlobalFilter,
  customPageSize,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    data: data || [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: customPageSize || 10,
      },
    },
  });

  const handlePageClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    action();
  };

  return (
    <Fragment>
      <Row className="mb-2">
        {isAddButton && (
          <Col sm="auto">
            <Button
              type="button"
              color="primary"
              className={buttonClass || ""}
              onClick={handleUserClick}
            >
              <i className="mdi mdi-plus me-1" /> {buttonName}
            </Button>
          </Col>
        )}
      </Row>

      <div className={divClassName || "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <Fragment>
                        <div
                          className={
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center mt-4 pt-2">
          <Col sm={12} md={5}>
            <div className="dataTables_info">
              Showing {table.getState().pagination.pageSize * table.getState().pagination.pageIndex + 1} to{" "}
              {Math.min(
                table.getState().pagination.pageSize * (table.getState().pagination.pageIndex + 1),
                data?.length || 0
              )}{" "}
              of {data?.length || 0} Results
            </div>
          </Col>
          <Col sm={12} md={7}>
            <div className={paginationWrapper}>
              <ul className={paginationClass || pagination}>
                <li
                  className={`paginate_button page-item previous ${
                    !table.getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <Link
                    to="#"
                    className="page-link"
                    onClick={(e) => handlePageClick(e, table.previousPage)}
                  >
                    <i className="mdi mdi-chevron-left" />
                  </Link>
                </li>
                {table.getPageOptions().map((page) => (
                  <li
                    key={page}
                    className={`paginate_button page-item ${
                      page === table.getState().pagination.pageIndex ? "active" : ""
                    }`}
                  >
                    <Link
                      to="#"
                      className="page-link"
                      onClick={(e) => handlePageClick(e, () => table.setPageIndex(page))}
                    >
                      {page + 1}
                    </Link>
                  </li>
                ))}
                <li
                  className={`paginate_button page-item next ${
                    !table.getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <Link
                    to="#"
                    className="page-link"
                    onClick={(e) => handlePageClick(e, table.nextPage)}
                  >
                    <i className="mdi mdi-chevron-right" />
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
